import React, { useEffect, useState } from 'react';
import {
  App,
  Button,
  Card,
  Col,
  DatePicker,
  Form,
  Input,
  Radio,
  Row,
  Spin,
  Typography,
} from 'antd';
import type { RadioChangeEvent } from 'antd/es/radio';
import { useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import customerSatisfactionSurveyService from '@/services/customerSatisfactionSurvey.service';
import {
  CSS_FORM_CODE,
  CSS_PARAMETER_KEYS,
  CSS_PARAMETER_LABELS,
  CSS_SCORE_OPTIONS,
  emptyParameterScores,
  type CssParameterKey,
  type CssParameterScores,
} from '@/constants/customer-satisfaction-survey';

const { Title, Text, Paragraph } = Typography;

const SCORE_COLORS: Record<number, string> = {
  1: '#cf1322',
  2: '#d46b08',
  3: '#d4b106',
  4: '#1677ff',
  5: '#389e0d',
};

const SCORE_RADIO_STYLES = `
.css-score-radio-group {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.css-score-radio-group .ant-radio-button-wrapper {
  border-inline-start-width: 1px !important;
  border-radius: 6px !important;
  margin-inline-start: 0 !important;
}
${Object.entries(SCORE_COLORS)
  .map(
    ([n, color]) => `
.css-score-radio-group .css-score-${n} {
  border-color: ${color} !important;
  color: ${color} !important;
}
.css-score-radio-group .css-score-${n}.ant-radio-button-wrapper-checked {
  background: ${color} !important;
  border-color: ${color} !important;
  color: #fff !important;
  font-weight: 600;
  box-shadow: 0 0 0 2px ${color}33;
}
.css-score-radio-group .css-score-${n}.ant-radio-button-wrapper-checked::before {
  display: none !important;
}
`
  )
  .join('\n')}
`;

/** Colored solid radio buttons so the selected score is obvious. */
const ScoreRadioGroup: React.FC<{
  value?: number;
  onChange?: (e: RadioChangeEvent) => void;
  disabled?: boolean;
}> = ({ value, onChange, disabled }) => (
  <Radio.Group
    value={value}
    onChange={onChange}
    disabled={disabled}
    optionType="button"
    buttonStyle="solid"
    className="css-score-radio-group"
  >
    {CSS_SCORE_OPTIONS.map((opt) => (
      <Radio.Button key={opt.value} value={opt.value} className={`css-score-${opt.value}`}>
        {opt.label}
      </Radio.Button>
    ))}
  </Radio.Group>
);

export default function CustomerSatisfactionPublicPage() {
  const { token } = useParams<{ token: string }>();
  const { message } = App.useApp();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<Record<string, unknown> | null>(null);
  const [form] = Form.useForm();

  useEffect(() => {
    if (!token) {
      setError('Invalid link');
      setLoading(false);
      return;
    }
    customerSatisfactionSurveyService
      .getPublic(token)
      .then((d) => {
        if (!d) {
          setError('Survey not found');
          return;
        }
        setData(d);
        const scores = (d.parameter_scores as CssParameterScores) || emptyParameterScores();
        const scoreFields: Record<string, unknown> = {};
        for (const key of CSS_PARAMETER_KEYS) {
          scoreFields[`score_${key}`] = scores[key]?.score ?? undefined;
          scoreFields[`comments_${key}`] = scores[key]?.comments ?? undefined;
        }
        form.setFieldsValue({
          customer_name_snapshot: d.customer_name_snapshot,
          customer_address: d.customer_address,
          scope_of_work: d.scope_of_work,
          project_or_po_reference: d.project_or_po_reference,
          feedback_date: d.feedback_date ? dayjs(String(d.feedback_date)) : dayjs(),
          overall_comments: d.overall_comments,
          client_rep_name: d.client_rep_name,
          client_rep_email: d.client_rep_email,
          client_rep_signature: d.client_rep_signature,
          client_rep_date: d.client_rep_date ? dayjs(String(d.client_rep_date)) : dayjs(),
          ...scoreFields,
        });
      })
      .catch((e) => setError(e?.response?.data?.message || 'Invalid or expired link'))
      .finally(() => setLoading(false));
  }, [token, form]);

  const readOnly = Boolean(data?.read_only || data?.submitted);

  useEffect(() => {
    const companyName = String((data?.company as { name?: string } | undefined)?.name || '').trim();
    document.title = companyName
      ? `${companyName} — Customer Satisfaction Survey`
      : 'Customer Satisfaction Survey';
  }, [data]);

  const onFinish = async (values: Record<string, unknown>) => {
    if (!token || readOnly) return;
    const parameter_scores = emptyParameterScores();
    for (const key of CSS_PARAMETER_KEYS) {
      const score = Number(values[`score_${key}`]);
      const comments = values[`comments_${key}`] != null ? String(values[`comments_${key}`]) : null;
      parameter_scores[key] = { score: Number.isFinite(score) ? score : null, comments };
      if (score < 3 && !String(comments || '').trim()) {
        message.error(`Comments required when score is below 3 for: ${CSS_PARAMETER_LABELS[key]}`);
        return;
      }
    }
    setSubmitting(true);
    try {
      const result = await customerSatisfactionSurveyService.submitPublic(token, {
        customer_name_snapshot: values.customer_name_snapshot as string,
        customer_address: values.customer_address as string,
        scope_of_work: values.scope_of_work as string,
        project_or_po_reference: values.project_or_po_reference as string,
        feedback_date: (values.feedback_date as dayjs.Dayjs)?.format?.('YYYY-MM-DD'),
        parameter_scores,
        overall_comments: values.overall_comments as string,
        client_rep_name: String(values.client_rep_name || ''),
        client_rep_email: String(values.client_rep_email || ''),
        client_rep_signature: values.client_rep_signature as string,
        client_rep_date: (values.client_rep_date as dayjs.Dayjs)?.format?.('YYYY-MM-DD'),
      });
      message.success('Thank you — your feedback has been submitted');
      setData(result ?? { ...data, submitted: true, read_only: true });
    } catch (e: any) {
      message.error(e?.response?.data?.message || 'Submit failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }
  if (error || !data) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Title level={4}>{error || 'Not found'}</Title>
      </div>
    );
  }

  const company = (data.company || {}) as {
    name?: string;
    address?: string;
    phone?: string;
    email?: string;
    website?: string;
    trn?: string;
    logo?: string | null;
    letterhead_header?: string | null;
    letterhead_footer?: string | null;
  };
  const headerSrc = company.letterhead_header || '/quotation-letterhead/qtn-header.png';
  const footerSrc = company.letterhead_footer || '/quotation-letterhead/qtn-footer.jpg';
  const contactLine = [
    company.phone ? `Tel ${company.phone}` : '',
    company.email || '',
    company.website || '',
    company.trn ? `TRN ${company.trn}` : '',
  ]
    .filter(Boolean)
    .join('  ·  ');

  return (
    <div style={{ maxWidth: 920, margin: '24px auto', padding: '0 16px 32px' }}>
      <style>{SCORE_RADIO_STYLES}</style>
      <img src={headerSrc} alt="" style={{ display: 'block', width: '100%', height: 'auto' }} />
      {(company.name || company.address || contactLine) && (
        <div style={{ padding: '10px 8px 0', textAlign: 'center', lineHeight: 1.45 }}>
          {company.name ? (
            <div style={{ fontSize: 16, fontWeight: 700 }}>{company.name}</div>
          ) : null}
          {company.address ? (
            <div style={{ color: '#444', fontSize: 12 }}>{company.address}</div>
          ) : null}
          {contactLine ? <div style={{ color: '#444', fontSize: 12 }}>{contactLine}</div> : null}
        </div>
      )}
      <Card style={{ marginTop: 12 }}>
        <Title level={3} style={{ marginBottom: 4 }}>
          Customer Satisfaction Survey Form
        </Title>
        <Text type="secondary">
          {CSS_FORM_CODE}
          {data.survey_number ? ` · ${String(data.survey_number)}` : ''}
        </Text>
        <Paragraph style={{ marginTop: 12 }}>
          Please rate each parameter from 1 (Poor) to 5 (Excellent). Comments are required when the
          score is below 3.
        </Paragraph>

        {readOnly && (
          <Paragraph type="success">
            This survey has been submitted
            {data.satisfaction_pct != null
              ? ` (score ${Number(data.satisfaction_pct).toFixed(1)}%).`
              : '.'}
          </Paragraph>
        )}

        <Form form={form} layout="vertical" onFinish={onFinish} disabled={readOnly}>
          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item name="customer_name_snapshot" label="Customer name" rules={[{ required: true }]}>
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={12}>
              <Form.Item name="feedback_date" label="Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="customer_address" label="Address">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="scope_of_work" label="Scope of work">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Col>
            <Col span={24}>
              <Form.Item name="project_or_po_reference" label="Project / PO reference">
                <Input />
              </Form.Item>
            </Col>
          </Row>

          {CSS_PARAMETER_KEYS.map((key: CssParameterKey) => (
            <Card
              key={key}
              size="small"
              title={CSS_PARAMETER_LABELS[key]}
              style={{ marginBottom: 12 }}
            >
              <Form.Item
                name={`score_${key}`}
                label="Score"
                rules={[{ required: true, message: 'Select a score (1–5)' }]}
              >
                <ScoreRadioGroup />
              </Form.Item>
              <Form.Item name={`comments_${key}`} label="Comments (required if score is below 3)">
                <Input.TextArea rows={2} />
              </Form.Item>
            </Card>
          ))}

          <Form.Item name="overall_comments" label="Overall comments">
            <Input.TextArea rows={3} />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} md={8}>
              <Form.Item
                name="client_rep_name"
                label="Client representative name"
                rules={[{ required: true, message: 'Name is required' }]}
              >
                <Input />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item
                name="client_rep_email"
                label="Client representative email"
                rules={[
                  { required: true, message: 'Email is required' },
                  { type: 'email', message: 'Enter a valid email' },
                ]}
              >
                <Input type="email" placeholder="name@company.com" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="client_rep_signature" label="Signature (type full name)">
                <Input placeholder="Type name as signature" />
              </Form.Item>
            </Col>
            <Col xs={24} md={8}>
              <Form.Item name="client_rep_date" label="Date" rules={[{ required: true }]}>
                <DatePicker style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          {!readOnly && (
            <Button type="primary" htmlType="submit" loading={submitting} size="large" block>
              Submit feedback
            </Button>
          )}
        </Form>
      </Card>
      <img
        src={footerSrc}
        alt=""
        style={{ display: 'block', width: '100%', height: 'auto', marginTop: 12 }}
      />
    </div>
  );
}
